"use client";

import React, { createContext, useReducer, useCallback, useMemo } from "react";
import type { VariableState, VariableContextType } from "./variable/types/variable-types";
import { variableReducer } from "./variable/reducer/variable-reducer";
import {
  getVariableValue as getVariableValueHelper,
  hasValue as hasValueHelper,
  getAllVariables as getAllVariablesHelper,
  getFormattedValues as getFormattedValuesHelper,
  replaceVariablesInText as replaceVariablesInTextHelper,
  resolveTemplateUnified,
  hasUnfilledVariables as hasUnfilledVariablesHelper,
} from "./variable/utils/variable-resolvers";

// ============================================================================
// VARIABLE CONTEXT
// ============================================================================

export const VariableContext = createContext<VariableContextType | null>(null);

// Provider
interface VariableProviderProps {
  children: React.ReactNode;
}

export function VariableProvider({ children }: VariableProviderProps) {
  const [state, dispatch] = useReducer(variableReducer, {
    values: {},
    editingField: null,
    draftValue: "",
    allVariables: [],
    autoFilledVariables: {},
  });

  // Actions
  const setValue = useCallback(
    (varName: string, value: string) => {
      if (process.env.NODE_ENV === "development") {
        console.log("[VariableContext] setValue called:", {
          varName,
          value,
          currentValues: state.values,
        });
      }
      dispatch({ type: "SET_VALUE", varName, value });
    },
    [state.values]
  );

  const setValues = useCallback((values: Record<string, string>) => {
    dispatch({ type: "SET_VALUES", values });
  }, []);

  const startEditing = useCallback((varName: string, currentValue: string) => {
    dispatch({ type: "START_EDITING", varName, currentValue });
  }, []);

  const updateDraft = useCallback((value: string) => {
    dispatch({ type: "UPDATE_DRAFT", value });
  }, []);

  const applyDraft = useCallback((varName: string) => {
    dispatch({ type: "APPLY_DRAFT", varName });
  }, []);

  const cancelEditing = useCallback(() => {
    dispatch({ type: "CANCEL_EDITING" });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  // Centralized variable management
  const setAllVariables = useCallback((variables: string[]) => {
    dispatch({ type: "SET_ALL_VARIABLES", variables });
  }, []);

  const setAutoFilledVariables = useCallback((variables: Record<string, string>) => {
    dispatch({ type: "SET_AUTO_FILLED", variables });
  }, []);

  const updateTemplateVariables = useCallback((subject: string, body: string) => {
    if (process.env.NODE_ENV === "development") {
      console.log("[VariableContext] updateTemplateVariables called:", {
        subject: subject.substring(0, 100) + (subject.length > 100 ? "..." : ""),
        body: body.substring(0, 100) + (body.length > 100 ? "..." : ""),
      });
    }
    dispatch({ type: "UPDATE_TEMPLATE_VARIABLES", subject, body });
  }, []);

  // Computed values
  const getVariableValue = useCallback(
    (varName: string, selectedClient?: any): string => {
      return getVariableValueHelper(varName, state.values, selectedClient);
    },
    [state.values]
  );

  const hasValue = useCallback(
    (varName: string, selectedClient?: any): boolean => {
      return hasValueHelper(varName, state.values, selectedClient);
    },
    [state.values]
  );

  const getAllVariables = useCallback((subject: string, body: string): string[] => {
    return getAllVariablesHelper(subject, body);
  }, []);

  const getFormattedValues = useCallback((): Record<string, string> => {
    return getFormattedValuesHelper(state.values);
  }, [state.values]);

  const replaceVariablesInText = useCallback(
    (text: string, selectedClient?: any): string => {
      return replaceVariablesInTextHelper(text, state.values, selectedClient);
    },
    [state.values]
  );

  // Unified resolver integration - uses the centralized resolveTemplate function
  const resolveTemplateUnifiedFn = useCallback(
    (subject: string, body: string, contact?: any, ticket?: any) => {
      return resolveTemplateUnified(subject, body, state.values, contact, ticket);
    },
    [state.values]
  );

  const hasUnfilledVariables = useCallback(
    (subject: string, body: string, contact?: any, ticket?: any): boolean => {
      return hasUnfilledVariablesHelper(subject, body, state.values, contact, ticket);
    },
    [state.values]
  );

  const contextValue = useMemo(
    () => ({
      // State
      values: state.values,
      editingField: state.editingField,
      draftValue: state.draftValue,
      allVariables: state.allVariables,
      autoFilledVariables: state.autoFilledVariables,

      // Actions
      setValue,
      setValues,
      startEditing,
      updateDraft,
      applyDraft,
      cancelEditing,
      reset,

      // Centralized variable management
      setAllVariables,
      setAutoFilledVariables,
      updateTemplateVariables,

      // Computed values
      getVariableValue,
      hasValue,
      getAllVariables,

      // Form integration
      getFormattedValues,
      replaceVariablesInText,

      // Unified resolver integration
      resolveTemplate: resolveTemplateUnifiedFn,
      hasUnfilledVariables,
    }),
    [
      state.values,
      state.editingField,
      state.draftValue,
      state.allVariables,
      state.autoFilledVariables,
      setValue,
      setValues,
      startEditing,
      updateDraft,
      applyDraft,
      cancelEditing,
      reset,
      setAllVariables,
      setAutoFilledVariables,
      updateTemplateVariables,
      getVariableValue,
      hasValue,
      getAllVariables,
      getFormattedValues,
      replaceVariablesInText,
      resolveTemplateUnifiedFn,
      hasUnfilledVariables,
    ]
  );

  return <VariableContext.Provider value={contextValue}>{children}</VariableContext.Provider>;
}

// Re-export hook from separate file
export { useVariableContext } from "./variable/hooks/useVariableContext";
